import type {
    GameSession,
    GetSessionResponse,
    ListSessionsResponse,
    PublicSessionResponse,
    SessionOwnerResponse,
    SessionSharingResponse,
    SessionVideosResponse,
    UserSessionListResponse,
} from '@repo/types/sessions/session-types';
import { WeModApiClient } from './api-client';

const apiClient = new WeModApiClient(process.env.NEXT_PUBLIC_WEMOD_API_URL ?? '');

export async function getUserSession(
    uuid: string,
    startTime: string,
    endTime: string,
    titleId: string,
): Promise<GameSession> {
    const query: Record<string, string> = {
        startTime,
        endTime,
        titleId,
    };

    const result = await apiClient.get<GetSessionResponse>(`/v3/analytics/game_sessions/${uuid}`, { params: query });

    result.data.positions ??= [];
    result.data.events ??= [];

    return {
        ...result.data,
        uuid,
        isShared: result.metadata.isShared ?? false,
    };
}

export async function listUserSessions(
    page: number = 1,
    perPage: number = 15,
    filters?: { titleId?: number },
): Promise<ListSessionsResponse> {
    const query: Record<string, string> = {
        page: page.toString(),
        perPage: perPage.toString(),
    };

    if (filters?.titleId) query.titleId = filters.titleId.toString();

    return apiClient.get<ListSessionsResponse>('/v3/game-sessions', { params: query });
}

export async function getPublicSession(shareToken: string): Promise<PublicSessionResponse> {
    return apiClient.get<PublicSessionResponse>(`/v3/analytics/game_sessions/${shareToken}/public`, {
        skipAutoAuth: true,
    });
}

export async function getSessionOwner(uuid: string): Promise<SessionOwnerResponse> {
    return apiClient.get<SessionOwnerResponse>(`/v3/sessions/${uuid}/owner`, {
        skipAutoAuth: true,
    });
}

export async function getSessionVideos(sessionId: string): Promise<SessionVideosResponse> {
    return apiClient.get<SessionVideosResponse>('/v3/user/videos/session', {
        params: { sessionUuid: sessionId },
        skipAutoAuth: true,
    });
}

export async function listSessionMetadata(titleId?: number): Promise<UserSessionListResponse> {
    const params: Record<string, string> = {};
    if (titleId !== undefined) params.titleId = titleId.toString();
    return apiClient.get<UserSessionListResponse>('/v3/analytics/game_sessions/list', { params });
}

export async function toggleSessionSharing(
    sessionId: string,
    isShareable: boolean,
    params: { titleId: number; startTime: string; endTime: string },
): Promise<SessionSharingResponse> {
    const query: Record<string, string> = {
        titleId: params.titleId.toString(),
        startTime: params.startTime,
        endTime: params.endTime,
    };

    return apiClient.put<SessionSharingResponse>(
        `/v3/analytics/game_sessions/${sessionId}/sharing`,
        { is_shareable: isShareable },
        { params: query },
    );
}
